import { FocusScope } from '@radix-ui/react-focus-scope';
import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  useKBar,
  VisualState,
} from 'kbar';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';

import { ModalsManager } from '@/components/modals/modalsManager';
import { meetingActions } from '@/features/meetings/meetings.cmdk';
import { projectActions } from '@/features/projects/projects.cmdk';
import { cn } from '@/lib/utils';

import { startNewChat } from './actions/chat';
import { useInternalToolsActions } from './actions/internal-tools';
import { resetLocalDatabaseAction, useThemeActions } from './actions/settings';
import { CommandBarBody } from './cmd-bar-body';
import useCommandBarActions from './use-command-bar-actions';
import { useNavigationAction } from './use-navigation-action';
import { usePlaygroundActions } from './use-playground-action';
import { useRecentCmdBarActions } from './use-recent-cmd-bar-actions';
import { useSearchCmdBarActions } from './use-search-cmd-bar-actions';
import { useSettingsAction } from './use-settings-action';

export const CommandBar = observer(function CommandBar() {
  const { query, visible, currentRootActionId } = useKBar((state) => ({
    visible: state.visualState !== VisualState.hidden,
    currentRootActionId: state.currentRootActionId,
  }));

  const settingsAction = useSettingsAction();
  const navigationAction = useNavigationAction();
  const themeSettingsActions = useThemeActions();
  const {
    actions: searchActions,
    totalCount,
    sectionTotals,
  } = useSearchCmdBarActions(visible);
  const recentActions = useRecentCmdBarActions(visible);
  const playgroundActions = usePlaygroundActions();
  const internalToolsActions = useInternalToolsActions();

  const commandBarActions = useMemo(
    () => [
      startNewChat,
      ...navigationAction,
      settingsAction,
      ...themeSettingsActions,
      ...internalToolsActions,
      resetLocalDatabaseAction,
      playgroundActions,
      ...recentActions,
      ...meetingActions,
      ...projectActions,
    ],
    [
      navigationAction,
      settingsAction,
      themeSettingsActions,
      internalToolsActions,
      recentActions,
      playgroundActions,
    ]
  );

  useCommandBarActions(commandBarActions);
  // A separate call, so new search results do not register all static actions again.
  // A new query can return the same ids with a different preview, so the array is a dependency.
  useCommandBarActions(searchActions, [searchActions]);

  useEffect(() => {
    if (!visible || !currentRootActionId) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        query.setCurrentRootAction(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [visible, currentRootActionId, query]);

  useEffect(() => {
    return ModalsManager.onOpenChange((hasOpenModal) => {
      if (hasOpenModal) {
        query.setVisualState(VisualState.hidden);
      }
    });
  }, [query]);

  return (
    <KBarPortal>
      <KBarPositioner className="z-[60]">
        <FocusScope
          trapped
          loop
          className="outline-none focus-visible:outline-none"
        >
          <KBarAnimator
            className={cn(
              'w-[min(820px,90vw)] max-h-[75vh] overflow-hidden',
              'bg-surface backdrop-blur-3xl text-foreground',
              'rounded-lg',
              'shadow-2xl',
              'border border-border-strong',
              'print:hidden'
            )}
          >
            <CommandBarBody
              totalCount={totalCount}
              sectionTotals={sectionTotals}
            />
          </KBarAnimator>
        </FocusScope>
      </KBarPositioner>
    </KBarPortal>
  );
});
